import * as React from "react";

interface IDreamNameExpansionContext {
    expandableDreamIds: ReadonlySet<number>;
    expandedDreamIds: ReadonlySet<number>;
    setDreamExpandable: (dreamId: number, isExpandable: boolean) => void;
    toggleDream: (dreamId: number) => void;
    collapseDream: (dreamId: number) => void;
    expandAll: () => void;
    collapseAll: () => void;
}

const DreamNameExpansionContext = React.createContext<
    IDreamNameExpansionContext | undefined
>(undefined);

export function DreamNameExpansionProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [expandableDreamIds, setExpandableDreamIds] = React.useState(
        () => new Set<number>()
    );
    const [expandedDreamIds, setExpandedDreamIds] = React.useState(
        () => new Set<number>()
    );

    const setDreamExpandable = React.useCallback(
        (dreamId: number, isExpandable: boolean) => {
            setExpandableDreamIds((current) =>
                UpdateSetMembership(current, dreamId, isExpandable)
            );

            if (!isExpandable) {
                setExpandedDreamIds((current) =>
                    UpdateSetMembership(current, dreamId, false)
                );
            }
        },
        []
    );
    const toggleDream = React.useCallback((dreamId: number) => {
        setExpandedDreamIds((current) =>
            UpdateSetMembership(current, dreamId, !current.has(dreamId))
        );
    }, []);
    const collapseDream = React.useCallback((dreamId: number) => {
        setExpandedDreamIds((current) =>
            UpdateSetMembership(current, dreamId, false)
        );
    }, []);
    const expandAll = React.useCallback(
        () => setExpandedDreamIds(new Set(expandableDreamIds)),
        [expandableDreamIds]
    );
    const collapseAll = React.useCallback(
        () => setExpandedDreamIds((current) =>
            current.size === 0 ? current : new Set<number>()
        ),
        []
    );

    const value = React.useMemo(
        () => ({
            expandableDreamIds,
            expandedDreamIds,
            setDreamExpandable,
            toggleDream,
            collapseDream,
            expandAll,
            collapseAll,
        }),
        [
            expandableDreamIds,
            expandedDreamIds,
            setDreamExpandable,
            toggleDream,
            collapseDream,
            expandAll,
            collapseAll,
        ]
    );

    return (
        <DreamNameExpansionContext.Provider value={value}>
            {children}
        </DreamNameExpansionContext.Provider>
    );
}

export function useDreamNameExpansion() {
    const context = React.useContext(DreamNameExpansionContext);

    if (!context) {
        throw new Error(
            `useDreamNameExpansion must be used inside DreamNameExpansionProvider`
        );
    }

    return context;
}

function UpdateSetMembership(
    current: Set<number>,
    value: number,
    shouldInclude: boolean
): Set<number> {
    if (current.has(value) === shouldInclude) {
        return current;
    }

    const updated = new Set(current);
    shouldInclude ? updated.add(value) : updated.delete(value);
    return updated;
}
